import { useCallback, useState } from "react";
import { v4 as generateUuid } from "uuid";
import {
  FirebasePaths,
  getFirebaseStoragePath,
  imageDb,
} from "../../utils/firebase";
import { ref, uploadBytes, getDownloadURL } from "@firebase/storage";
import { useUserContext } from "../../hooks/userContext";
import { useUploadMediaFileMutation } from "../../hooks/use-upload-media-file-mutation";
import { MediaFile } from "../../__generated__/types";

export const useFirebaseImageUpload = (
  path: FirebasePaths,
  onSuccess?: (result: MediaFile[]) => void
) => {
  const { user } = useUserContext();
  const userId = user?.id ?? "unknown";

  const [loading, setLoading] = useState(false);

  const { uploadMediaFile } = useUploadMediaFileMutation();

  // Keep upload handler stable so consumers (e.g., CKEditor) don't remount on every render.
  const onUpload = useCallback(async (files: FileList): Promise<MediaFile[]> => {
    const completed: MediaFile[] = [];
    const total = files?.length ?? 0;

    const uploadImage = async (file: File) => {
      const uploadFile = file;
      const id = generateUuid();

      setLoading(true);

      if (!uploadFile) return;

      const imageRef = ref(
        imageDb,
        getFirebaseStoragePath(userId, `${path}/${id}` as FirebasePaths)
      );

      await uploadBytes(imageRef, uploadFile, {
        contentEncoding:
          "EozgmJ9LqM14CqtxlzKG5bRlfOcIo2EWMGuiQEs1zwV3YdFso6wMIa7ioYYTvY7B",
      })
        .then(async (image) => {
          if (!image.metadata.ref) return;
          const url = await getDownloadURL(image.metadata.ref);
          const uploadResult = await uploadMediaFile({
            userId,
            url,
            path: image.metadata.fullPath,
          });
          completed.push(uploadResult);
          return uploadResult;
        })
        .catch((error: string) => {
          console.error("Error uploading file:", error);
        });
    };

    for (let i = 0; i < total; i++) {
      const file = files?.[i];
      if (file) {
        await uploadImage(file);
        if (completed.length === total) {
          onSuccess?.(completed as MediaFile[]);
          setLoading(false);
          return completed as MediaFile[];
        }
      }
    }

    setLoading(false);
    return completed;
  }, [userId, path, uploadMediaFile, onSuccess]);

  return {
    loading,
    onUpload,
  };
};
